import type { CoverProductSelection } from './BookingDocket'
import { Product } from './Product'
import { SKU } from './SKU'
import { MenuLinkType } from './MenuLinkType'

export class Consumable extends Product {

    constructor(
        id: string,
        displayName: string,
        description: string | null,
        skus: SKU[],
        public modifierGroupIds: string[],
        public imageId: string | null = null
    ) {
        super(id, displayName, description, skus)
    }

    hasSKUsOnSale(): boolean {
        return this.skus.some(sku => sku.onSale)
    }

    getSKUWithId(skuId: string): SKU | null {
        return this.skus.find(sku => sku.id === skuId) || null
    }

    getAmountForSKU(skuId: string): number {
        return this.getSKUWithId(skuId)?.price ?? 0
    }
}

export const PARTY_SIZE_QUANTITY_MENU_IDS: string[] = [
    'cd6abf2c-a551-4b44-a67a-21880ecf4931', // Law Golf and Things Activities Local
    '45e0e593-9d26-464c-ac87-bfa6dfc4a4da', // Golf and Things Staging
    '05571f1e-5d91-462f-8a89-41e13fb05bcf', // Safari & Co Social Darts (2 hrs)
    '3d1d2ade-ebd9-48e7-b8ba-35919ab12c40', // Safari & Co American Pool
    '45409333-4fc1-4f82-84e5-3a244301d01f', // Safari & Co Interactive Golf 1hr
    'ae03e8d2-8b9b-4b4b-8f89-d5063fbaf27b', // Safari & Co Social Darts (1 hr)
    'd4d8db7b-dff8-41d1-bc14-ea027b91802c', // Safari & Co Crazy Pool
    'e3927ac1-6eb5-45d0-9122-a37cacac5fe4', // Safari & Co Interactive Golf (2 hrs)
    '57149bcb-793c-4e3f-b8fc-fce862058794', // Safari & Co Packages
    '8e8caa16-2c61-481a-91cc-043cc5365fda', // Ardennan House Hotel Christmas Tipsy Tapas
]

export class Menu {
    constructor(
        public id: string,
        public displayName: string,
        public description: string | null,
        public linkType: MenuLinkType,
        public menuCourses: MenuCourse[],
        public minimumCoursesRequired: number | null = null,
        public maximumCoursesRequired: number | null = null,
        public isPaid: boolean = false
    ) {}

    requiredBookingQuantity(partySize: number): number | null {
        return PARTY_SIZE_QUANTITY_MENU_IDS.includes(this.id) ? partySize : null
    }

    findItemForSelection(selection: CoverProductSelection): MenuItem | null {
        // Legacy choices use the first SKU match in menu order.
        return this.menuCourses.flatMap(course => course.menuItems).find(item =>
            item.skuId === selection.productSkuId && (!selection.menuItemId || item.id === selection.menuItemId)
        ) ?? null
    }

    countSelectedCourses(menuItemIds: string[]): number {
        const selectedItemIds = new Set(menuItemIds)
        return this.menuCourses.filter(course => !course.excludesFromCourseCount && course.menuItems.some(item => selectedItemIds.has(item.id))).length
    }
}

export class MenuCourse {
    constructor(
        public id: string,
        public menuId: string,
        public displayName: string,
        public orderIndex: number,
        public minimumItemsRequired: number | null,
        public maximumItemsRequired: number | null,
        public menuItems: MenuItem[],
        public excludesFromCourseCount: boolean = false,
        public description: string | null = null
    ) {}
}

export class MenuItem {
    constructor(
        public id: string,
        public menuId: string,
        public menuCourseId: string,
        public consumableId: string,
        public skuId: string,
        public refundable: boolean = true,
        public paidOnline: boolean = true
    ) {}
}
